import React, { useState, useRef } from "react";
import './withdrawal.scss';

import { useWithdrawalVerification, getWithdrawalError, updateAfterWithdrawal } from '../../../../hooks/useWithdrawalVerification';
import withFeatureFlags from '../../../FeatureFlags/withFeatureFlags';

import FirstStep from "./FirstStep";
import SummaryStep from "./SummaryStep";
import OtpStep from "./OtpStep";
import SuccessStep from "./SuccessStep";
import {useLogger} from "../../../../contexts/Logger";
import {useUnifiedAnalytics} from "../../../../contexts/AnalyticsWrapper";
import {productTypes} from '../../../../utils/productHelpers';

/** @import {WithdrawalProps} from './indexTypes' */
/** @import { Account } from "../../DashboardTypes" */
/** @import {DestinationAccount} from "../../../MyAccount/MyAccountTypes" */

const STEP_START = /** @type {const} */ 'start';
const STEP_OTP = /** @type {const} */ 'otp';
const STEP_SUMMARY = /** @type {const} */ 'summary';
const STEP_SUCCESS = /** @type {const} */ 'success';

function Withdrawal(/** @type { WithdrawalProps } */ props) {
    const [amount, setAmount] = useState(/** @type {number | null} */ (null));
    const [reference, setReference] = useState(null);
    const [step, setStep] = useState(/** @type {typeof STEP_START | typeof STEP_OTP | typeof STEP_SUMMARY | typeof STEP_SUCCESS} */ (STEP_START));
    const [isLoading, setIsLoading] = useState(false);
    const [verificationToken, setVerificationToken] = useState('');
    const [error, setError] = useState(/** @type {{ type: string; message: string; } | undefined} */ (undefined));
    const [selectedDestinationAccount, setSelectedDestinationAccount] = useState(/** @type {DestinationAccount | null} */ (null));
    const [keepOrCloseAccount, setKeepOrCloseAccount] = useState('');

    const { sendStepUpOtp: sendOtp, submitWithdrawal } = useWithdrawalVerification();
    const paymentAccepted = useRef(false);

    const logger = useLogger();
    const analytics = useUnifiedAnalytics();

    const shouldStepUp = () => amount && amount >= (props.account.product.stepUpAmount ?? 0);
    const withdrawAllFunds = () => amount && amount >= props.account.balance;
    const isNoticeWithdrawal = Boolean(props.noticePeriod && props.noticePeriod.length && props.noticePeriod.length > 0);

    /** @return {DestinationAccount[]} */
    const getDestinationAccounts = (/** @type {Account} */ account) => {
        let mappedAccounts = account.transferAccounts.map(function (account) {
            return {
                accountNumber: account.accountNumber,
                sortCode: account.sortCode,
                productName: account.product.name,
                label: `${account.product.name} - ${account.accountNumber}`,
                productType: /** @type {string | null} */ (account.product.productType),
                balance: /** @type {number | null} */ (account.balance),
                maxWithdrawalLimit: /** @type {number | null} */ (account.product.maxWithdrawalLimit),
                isExternalAccount: false
            };
        });

        mappedAccounts.push({
            accountNumber: account.linkedAccountNumber || '',
            sortCode: account.linkedAccountSortCode || '',
            productName: 'Linked Account',
            label: `Linked Account - ${account.linkedAccountNumber}`,
            productType: null,
            balance: null,
            isExternalAccount: true
        });

        return mappedAccounts;
    };

    // const onReview = async (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) =>  {
    //     event.preventDefault();

    //     if (amount && selectedDestinationAccount) {
    //         if (isNoticeWithdrawal) {
    //             setStep(STEP_SUMMARY);
    //         } else {
    //             if (shouldStepUp()) {
    //                 await sendStepUpOtp();
    //                 setStep(STEP_OTP);
    //             } else {
    //                 await sendWithdrawal();   
    //             }
    //         }
    //     }
    // };

    const onConfirm = async (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) =>  {
        event.preventDefault();

        logger.info(`Initiating ${isNoticeWithdrawal ? 'notice withdrawal' : 'withdrawal'}`);
        
        if (shouldStepUp()) {
            if (await sendStepUpOtp()) setStep(STEP_OTP);
        } else {
            logger.info('No OTP required');
            await sendWithdrawal();
        }
    };

    const onOtpConfirm = async (/** @type {string} */ otpValue, /** @type {string} */ otpMode, /** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) => {
        event.preventDefault();

        logger.info('OTP confirmed');
        await sendWithdrawal({
            mode: otpMode,
            passCode: otpValue,
            verificationToken: verificationToken
        });
    };

    const sendStepUpOtp = async () => {
        setIsLoading(true);
        
        try {
            logger.info(`Sending OTP`);
            
            setVerificationToken(await sendOtp());
            setError(undefined);
            setIsLoading(false);
            return true;
        }
        catch (e) {
            setIsLoading(false);
            setError(getWithdrawalError(e));
            return false;
        }
    };

    const sendWithdrawal = async (/** @type {{ mode: string; passCode: string; verificationToken: string; }} */ verifyOtp) => {
        if (!selectedDestinationAccount || paymentAccepted.current || isLoading) return;
        
        const { account } = props;
        let newTransaction;

        setIsLoading(true);
        setError(undefined);

        logger.info(`Sending ${isNoticeWithdrawal ? 'notice withdrawal' : 'withdrawal'}`, {
            additionalData: {
                fromAccountId: account.id,
                amount: amount,
                destinationAccountNumber: selectedDestinationAccount.accountNumber,
                destinationSortCode: selectedDestinationAccount.sortCode
            }
        });

        try {
            newTransaction = await submitWithdrawal({
                id: account.id,
                reference,
                amount,
                previousBalance: account.balance,
                destinationSortCode: selectedDestinationAccount.sortCode,
                destinationAccountNumber: selectedDestinationAccount.accountNumber,
                verifyOtp: verifyOtp ?? null
            }, account.availableBalance);
        } catch (e) {
            setError(getWithdrawalError(e));
            setIsLoading(false);
            return;
        }

        paymentAccepted.current = true;
        setStep(STEP_SUCCESS);
        setIsLoading(false);
        updateAfterWithdrawal(() => {
            account.balance = newTransaction.updatedBalance;
            account.availableBalance = newTransaction.availableBalance;
            account.product.interestRate = newTransaction.updatedInterestRate;

            newTransaction.counterpartAccountType = selectedDestinationAccount.productName;
            
            if (isNoticeWithdrawal) {
                newTransaction.toLinkedAccount = selectedDestinationAccount.isExternalAccount;
                account.pendingTransactions = account.pendingTransactions.concat([newTransaction]);
            } else {
                account.transactions = [newTransaction].concat(account.transactions);
                analytics.trackAnalyticsClickTransactionEvent
                (
                    'complete',
                    account.product.code || account.product.name.toLowerCase(),
                    'Withdrawal',
                    '',
                    'transaction|complete|' + (account.product.code || account.product.name.toLowerCase())
                );


            }
        }, () => logger.error('Payment accepted but account display or analytics update failed'));
    }
    
    const onComplete = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) =>  {
        event.preventDefault();
        const { account, onDone } = props;
        if (typeof onDone === 'function') onDone(account, amount);
        return false;
    };

    const onCancelled = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) =>  {
        event.preventDefault();
        const { account, onCancel } = props;
        if (typeof onCancel === 'function') onCancel();
        return false;
    };
    
    const onCloseAccount = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event, /** @type {string} */ accountNumber) => {
        event.preventDefault();
        setKeepOrCloseAccount('close');
        const { onCloseAccount } = props;
        if (typeof onCloseAccount === 'function') onCloseAccount(accountNumber);
        return false;
    };

    const onKeepAccount = (/** @type {string} */ keep) => {
        setKeepOrCloseAccount(keep);
    };

    const onAmountChange = (/** @type {number} */ amount) => {
        setAmount(amount);
    };

    const renderFirstStep = () => {
        const { account, noticePeriod, enableClosure } = props;
        return <FirstStep
            stepUpAmount={props.account.product.stepUpAmount}
            interestPaid={account.interestPaid}
            isNoticeWithdrawal={isNoticeWithdrawal}
            isIsaWithdrawal={account.product.productType === productTypes.InstantAccessCashISA}
            accounts={getDestinationAccounts(account)}
            balance={account.balance}
            availableBalance={account.availableBalance}
            maxWithdrawalLimit={account.product.maxWithdrawalLimit}
            lowerInterestRateAmount={account.product.lowerInterestRateAmount}
            productName={account.product.name}
            onCancel={onCancelled}
            onConfirm={onConfirm}
            onAmountChange={onAmountChange}
            onAccountSelected={setSelectedDestinationAccount}
            onCloseAccount={onCloseAccount}
            onKeepAccount={onKeepAccount}
            error={error}
            loading={isLoading}
            noticePeriod={noticePeriod}
            enableClosure={enableClosure}
        />
    };

    const renderSummaryStep = () => {
        const { account } = props;
        return (<SummaryStep
            onCancel={onCancelled}
            onConfirm={onConfirm}
            amount={amount}
            productName={account.product.name}
            stepUpAmount={props.account.product.stepUpAmount}
            isNoticeWithdrawal={isNoticeWithdrawal}
            selectedAccount={selectedDestinationAccount}
            loading={isLoading}
        />);
    };
    
    const renderOtpStep = () => {
        const { account } = props;

        return (<OtpStep
            isNoticeWithdrawal={isNoticeWithdrawal}
            onCancel={onCancelled}
            onConfirm={onOtpConfirm}
            isLoading={isLoading}
            error={error}
            verificationToken={verificationToken}
        />);
    };

    const renderSuccessStep = () => {
        const { account, noticePeriod } = props;

        return (<SuccessStep
            onConfirm={onComplete}
            balance={isNoticeWithdrawal ? account.availableBalance : account.balance}
            amount={amount}
            keepOrCloseAccount={ keepOrCloseAccount }
            productName={account.product.name}
            noticePeriod={noticePeriod}
            isNoticeWithdrawal={isNoticeWithdrawal}
            selectedAccount={selectedDestinationAccount}
        />);
    };

    const renderStepFunction = {
        'start': renderFirstStep,
        'otp': renderOtpStep,
        'summary': renderSummaryStep,
        'success': renderSuccessStep
    }

    return renderStepFunction[step]();
}

export default withFeatureFlags(Withdrawal);
